import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plane, Plus, X, Trash2, MapPin, Calendar, Waves, ChevronDown, ChevronUp, Loader2, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/PageShell';
import ProGate from '../components/ProGate';
import { useSubscription } from '../contexts/SubscriptionContext';
import { authFetch, API } from '../utils/api';


function dayLabel(d) {
  if (d === 0) return 'Today!';
  if (d === 1) return 'Tomorrow';
  if (d < 0) return `${Math.abs(d)}d ago`;
  return `${d} days`;
}

function statusColor(status) {
  if (status === 'today') return 'text-green-400 bg-green-400/10';
  if (status === 'soon') return 'text-secondary bg-secondary/10';
  if (status === 'past') return 'text-muted-foreground/40 bg-white/5';
  return 'text-primary bg-primary/10';
}

export default function Trips() {
  const { isPro } = useSubscription();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [briefings, setBriefings] = useState({});
  const [briefingLoading, setBriefingLoading] = useState({});
  const navigate = useNavigate();

  // Form
  const [dest, setDest] = useState('');
  const [destLabel, setDestLabel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [sugLoading, setSugLoading] = useState(false);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/trips`);
      if (res.ok) setTrips((await res.json()).trips || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDestSearch = (val) => {
    setDest(val);
    setDestLabel('');
    clearTimeout(searchTimer.current);
    if (val.trim().length < 2) { setSuggestions([]); return; }
    setSugLoading(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await authFetch(`${API}/api/city-search?q=${encodeURIComponent(val.trim())}`);
        if (res.ok) setSuggestions((await res.json()).results || []);
      } catch {} finally { setSugLoading(false); }
    }, 350);
  };

  const selectDest = (sug) => {
    setDest(sug.city);
    setDestLabel(sug.label);
    setSuggestions([]);
  };

  const handleCreate = async () => {
    if (!dest || !startDate) return;
    try {
      await authFetch(`${API}/api/trips`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify({ destination: dest, destination_label: destLabel || dest, start_date: startDate, end_date: endDate || startDate, notes }),
      });
      load();
      setShowAdd(false);
      setDest(''); setDestLabel(''); setStartDate(''); setEndDate(''); setNotes('');
    } catch {}
  };

  const handleDelete = async (tripId) => {
    await authFetch(`${API}/api/trips/${tripId}`, { method: 'DELETE',  });
    load();
  };

  const toggleBriefing = async (tripId) => {
    if (expandedId === tripId) { setExpandedId(null); return; }
    setExpandedId(tripId);
    if (briefings[tripId]) return;
    setBriefingLoading(p => ({ ...p, [tripId]: true }));
    try {
      const res = await authFetch(`${API}/api/trips/${tripId}/briefing`);
      if (res.ok) {
        const data = await res.json();
        setBriefings(p => ({ ...p, [tripId]: data }));
      }
    } catch {} finally { setBriefingLoading(p => ({ ...p, [tripId]: false })); }
  };

  const upcoming = trips.filter(t => t.status !== 'past');
  const past = trips.filter(t => t.status === 'past');

  return (
    <PageShell title="Trip Planner" data-testid="trips-page">
      {!isPro ? (
        <div className="p-5"><ProGate feature="trips" /></div>
      ) : (
      <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate('/home')} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="flex items-center gap-2">
          <Plane className="w-4 h-4 text-primary" />
          <h1 className="text-sm font-bold">Surf Trips</h1>
        </div>
        <button
          data-testid="trip-add-btn"
          onClick={() => setShowAdd(!showAdd)}
          className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
        >
          {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4 text-primary" />}
        </button>
      </div>

      {/* Add Trip Form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-4">
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4 space-y-3" data-testid="trip-form">
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground/50 font-bold">New Trip</p>

              {/* Destination with autocomplete */}
              <div className="relative">
                <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Destination</label>
                <input
                  data-testid="trip-dest-input"
                  value={dest}
                  onChange={e => handleDestSearch(e.target.value)}
                  placeholder="Search a surf destination..."
                  autoComplete="off"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/30"
                />
                {sugLoading && <div className="absolute right-3 top-7"><div className="w-3 h-3 border-2 border-primary/40 border-t-primary rounded-full animate-spin" /></div>}
                {suggestions.length > 0 && (
                  <div className="absolute inset-x-0 top-full mt-1 bg-bg-paper border border-white/10 rounded-lg shadow-xl z-30 overflow-hidden" data-testid="trip-dest-suggestions">
                    {suggestions.map((sug, i) => (
                      <button key={i} onClick={() => selectDest(sug)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-primary/10 transition-colors border-b border-white/[0.04] last:border-0 flex items-center gap-2"
                      >
                        <MapPin className={`w-3 h-3 flex-shrink-0 ${sug.coastal ? 'text-primary' : 'text-muted-foreground/40'}`} />
                        <span className="truncate flex-1">{sug.label}</span>
                        {sug.coastal && <span className="text-[8px] text-primary/70 bg-primary/10 rounded-full px-1.5 py-0.5 flex-shrink-0">surf</span>}
                      </button>
                    ))}
                  </div>
                )}
                {destLabel && <p className="text-[9px] text-primary/60 mt-0.5">{destLabel}</p>}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Start Date</label>
                  <input data-testid="trip-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-primary/50" />
                </div>
                <div>
                  <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">End Date</label>
                  <input data-testid="trip-end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-primary/50" />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Notes (optional)</label>
                <input data-testid="trip-notes" value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="e.g. Staying at Uluwatu, bringing the fish"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/30" />
              </div>

              <button data-testid="trip-save-btn" onClick={handleCreate} disabled={!dest || !startDate}
                className="w-full bg-primary text-primary-foreground text-xs font-bold py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40">
                Save Trip
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground/30" /></div>
      ) : trips.length === 0 ? (
        <div className="text-center py-16">
          <Plane className="w-8 h-8 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-xs text-muted-foreground/50 mb-2">No surf trips planned</p>
          <button onClick={() => setShowAdd(true)} className="text-xs text-primary hover:underline">Plan your first trip</button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Upcoming */}
          {upcoming.length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40 font-bold mb-2">Upcoming</p>
              <div className="space-y-2" data-testid="upcoming-trips">
                {upcoming.map((trip, i) => (
                  <TripCard key={trip.trip_id} trip={trip} i={i}
                    expanded={expandedId === trip.trip_id}
                    briefing={briefings[trip.trip_id]}
                    briefingLoading={briefingLoading[trip.trip_id]}
                    onToggle={() => toggleBriefing(trip.trip_id)}
                    onDelete={() => handleDelete(trip.trip_id)}
                    onAskSwell={() => {
                      const msg = `My surf trip to ${trip.destination} starts ${trip.days_until === 0 ? 'today' : `in ${trip.days_until} days`} (${trip.start_date}). What's the forecast looking like? What board and suit should I bring?`;
                      navigate('/chat', { state: { prefillMessage: msg } });
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past */}
          {past.length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40 font-bold mb-2">Past</p>
              <div className="space-y-1.5 opacity-60">
                {past.map(trip => (
                  <div key={trip.trip_id} className="flex items-center gap-2.5 bg-white/[0.02] border border-white/[0.03] rounded-xl px-3 py-2">
                    <Plane className="w-3 h-3 text-muted-foreground/30 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{trip.destination}</p>
                      <p className="text-[9px] text-muted-foreground/40">{trip.start_date}</p>
                    </div>
                    <button onClick={() => handleDelete(trip.trip_id)} className="p-1 hover:bg-red-500/10 rounded-md"><Trash2 className="w-3 h-3 text-red-400/40" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </PageShell>
  );
}

function TripCard({ trip, i, expanded, briefing, briefingLoading, onToggle, onDelete, onAskSwell }) {
  const forecast = briefing?.forecast;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.05 }}
      className="bg-bg-subtle/50 border border-white/[0.05] rounded-2xl overflow-hidden"
      data-testid={`trip-card-${trip.trip_id}`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-semibold truncate">{trip.destination}</p>
            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md ${statusColor(trip.status)}`}>
              {dayLabel(trip.days_until)}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[9px] text-muted-foreground/50">
            <Calendar className="w-2.5 h-2.5" />
            <span>{trip.start_date}{trip.end_date && trip.end_date !== trip.start_date ? ` — ${trip.end_date}` : ''}</span>
          </div>
          {trip.notes && <p className="text-[9px] text-muted-foreground/40 mt-0.5 italic truncate">{trip.notes}</p>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={onToggle} className="p-1.5 hover:bg-white/5 rounded-lg transition-colors" data-testid={`trip-briefing-toggle-${trip.trip_id}`}>
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/40" /> : <ChevronDown className="w-3.5 h-3.5 text-primary/60" />}
          </button>
          <button onClick={onDelete} className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors">
            <Trash2 className="w-3 h-3 text-red-400/40" />
          </button>
        </div>
      </div>

      {/* Briefing */}
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="border-t border-white/[0.04] px-4 py-3">
              {briefingLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="w-3 h-3 animate-spin text-primary/50" />
                  <span className="text-[10px] text-muted-foreground/50">Fetching forecast...</span>
                </div>
              ) : forecast ? (
                <div>
                  <p className="text-xs font-semibold text-primary mb-2">{forecast.vibe}</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                      <Waves className="w-3 h-3 mx-auto mb-0.5 text-primary/60" />
                      <p className="text-sm font-bold">{forecast.max_wave_ft}'</p>
                      <p className="text-[8px] text-muted-foreground/40">max wave</p>
                    </div>
                    <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                      <Waves className="w-3 h-3 mx-auto mb-0.5 text-secondary/60" />
                      <p className="text-sm font-bold">{forecast.avg_wave_ft}'</p>
                      <p className="text-[8px] text-muted-foreground/40">avg wave</p>
                    </div>
                  </div>
                  {/* Daily breakdown */}
                  {forecast.days?.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {forecast.days.slice(0, 7).map(d => (
                        <div key={d.date} className="flex items-center gap-2 text-[10px]">
                          <span className="w-16 text-muted-foreground/40 flex-shrink-0">{new Date(d.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                          <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-primary/40 rounded-full" style={{ width: `${Math.min(d.wave_height_ft / 10 * 100, 100)}%` }} />
                          </div>
                          <span className="w-8 text-right font-medium">{d.wave_height_ft}'</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={onAskSwell} data-testid={`trip-ask-swell-${trip.trip_id}`}
                    className="w-full text-[10px] text-primary hover:text-primary/80 hover:underline text-center py-1 transition-colors">
                    Ask Swell for trip advice
                  </button>
                </div>
              ) : (
                <p className="text-[10px] text-muted-foreground/50 py-2">{briefing?.message || 'Forecast not available for this location'}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
